// src/pages/MyCourses.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useSearch } from '../contexts/SearchContext';
import { useUser } from '../contexts/UserContext';
import MyCoursesHeader from '../components/ui/MyCoursesHeader';
import DateRangePicker from '../components/ui/DateRangePicker';
import CourseCard from '../components/cards/CourseCard';
import CreateCourseModal from '../components/modals/CreateCourseModal';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import ParticipantsModal from '../components/modals/ParticipantsModal';
import WeeklyScheduleModal from '../components/modals/WeeklyScheduleModal';
import CourseScheduleModal from '../components/modals/CourseScheduleModal';
import CourseSyllabusModal from '../components/modals/CourseSyllabusModal';
import CourseSyllabusViewerModal from '../components/modals/CourseSyllabusViewerModal';
import CourseImagePickerModal from '../components/modals/CourseImagePickerModal';
import AssignCourseTeacherModal from '../components/modals/AssignCourseTeacherModal';
import AssignCourseProgramModal from '../components/modals/AssignCourseProgramModal';
import Pagination from '../components/ui/Pagination';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';
import { useSupplierPrograms } from '../contexts/SupplierProgramsContext';

// Same API_BASE resolution SearchContext.jsx/SupplierProgramsContext.jsx
// use — duplicated here since there's no shared teacher-directory
// context yet (same gap noted for the team roster below).
const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const API_BASE = ENV_BASE.replace(/\/+$/, '') || 'https://www.doroosonline.co.il';

async function fetchMyTeamTeachers() {
  const token = localStorage.getItem('jwt') ?? localStorage.getItem('token');
  const res = await fetch(`${API_BASE}/api/supplier/team`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    mode: 'cors',
    credentials: 'omit',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || data?.message || 'שגיאה בטעינת צוות המורים');
  return Array.isArray(data?.items) ? data.items : [];
}

// The course teacher-assignment picker is now scoped to this
// supplier's actual team (supplier_teachers, status='ACTIVE') via
// GET /api/supplier/team — no longer the full platform-wide approved
// directory. Enforced server-side too, not just here: an id outside
// the team gets rejected in CoursesController::update() regardless of
// what this list shows.
const POS = { ACTIVE: 'ACTIVE', ARCHIVED: 'ARCHIVED' };

const TAB_LIST = [
  { key: POS.ACTIVE, label: 'פעילים', countKey: 'active' },
  { key: POS.ARCHIVED, label: 'ארכיון', countKey: 'archived' },
];

function buildTeacherBusySlots(courses, currentCourseId) {
  const slots = [];
  (courses || []).forEach((course) => {
    if (!course) return;
    if (currentCourseId && course.id === currentCourseId) return;
    const sched = course.schedule;
    if (!sched) return;
    const weekly = Array.isArray(sched) ? sched : Array.isArray(sched.weekly) ? sched.weekly : [];
    weekly.forEach((m) => {
      if (!m?.day || !m?.startTime || !m?.endTime) return;
      slots.push({ day: m.day, startTime: m.startTime, endTime: m.endTime, courseId: course.id, courseTitle: course.title });
    });
  });
  return slots;
}

const MyCourses = () => {
  const {
    getMyCoursesActive, getMyCoursesArchived, registeredDateRange,
    setRegisteredDateRange, reloadMyCourses, updateCourse,
    getCourseParticipants, courseImageCollections, myCoursesPage,
    myCoursesLimit, myCoursesTotalActive, myCoursesTotalArchived,
    loadingMyCourses, goToMyCoursesPage, fetchMyCoursesPage,
  } = useSearch();

  const { user, role, userType, showAlert, refreshMe } = useUser();

  const isTeacher = role === 'teacher' || userType === 'teacher';
  const teacherId = isTeacher ? user?.id ?? null : null;
  const isTeacherApproved = isTeacher && String(user?.teacher_status || '').toUpperCase() === 'APPROVED';
  // Learning center (GEFEN supplier) — sees every course it created,
  // public or program-based, regardless of which team teacher is
  // assigned. School — sees the courses that live inside whichever
  // GEFEN programs it's registered in (school_programs), not courses
  // it created itself (schools don't create courses).
  const isSupplier = role === 'supplier' || userType === 'supplier';
  const isSchool = role === 'school' || userType === 'school';

  // Learning center can create courses only once approved by the
  // platform (supplier_profiles.status === 'APPROVED'), mirroring the
  // teacher approval gate above.
  const isSupplierApproved = isSupplier && String(user?.supplier_status || '').toUpperCase() === 'APPROVED';
  const canCreateCourse = isTeacherApproved || isSupplierApproved;

  // Real GEFEN programs for the "ציבורי / תוכנית" assign modal — only
  // ones GEFEN has actually confirmed (APPROVED/ACTIVE). A DRAFT or
  // still-PENDING program isn't something a course should be attached
  // to yet, and CLOSED/SUSPENDED ones are no longer usable either.
  const { programs: supplierPrograms, loading: supplierProgramsLoading } = useSupplierPrograms();
  const confirmedPrograms = useMemo(
    () => supplierPrograms.filter((p) => p.status === 'APPROVED' || p.status === 'ACTIVE'),
    [supplierPrograms]
  );

  // One bucket for everyone who isn't teacher/supplier/school — i.e.
  // the student view: their own registered courses, exactly as before.
  const roleKind = isTeacher ? 'teacher' : isSupplier ? 'supplier' : isSchool ? 'school' : 'student';

  const [position, setPosition] = useState(POS.ACTIVE);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, mode: null, course: null });
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [participantsCourse, setParticipantsCourse] = useState(null);
  const [participantsRows, setParticipantsRows] = useState([]);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleCourse, setScheduleCourse] = useState(null);
  const [syllabusOpen, setSyllabusOpen] = useState(false);
  const [syllabusCourse, setSyllabusCourse] = useState(null);
  const [syllabusViewCourse, setSyllabusViewCourse] = useState(null);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerCourse, setImagePickerCourse] = useState(null);
  const [assignTeacherCourse, setAssignTeacherCourse] = useState(null);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [teacherOptionsLoading, setTeacherOptionsLoading] = useState(false);
  const [assignProgramCourse, setAssignProgramCourse] = useState(null);
  const [teacherBusySlots, setTeacherBusySlots] = useState([]);
  const [scheduleViewerOpen, setScheduleViewerOpen] = useState(false);
  const [scheduleViewerCourse, setScheduleViewerCourse] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => { refreshMe?.(); }, [refreshMe]);

  const openParticipants = async (course) => {
    try {
      const rows = await getCourseParticipants(course.id);
      setParticipantsCourse(course); setParticipantsRows(rows); setParticipantsOpen(true);
    } catch (e) { showAlert?.(e?.message || 'שגיאה בטעינת דו״ח משתתפים', 'warning'); }
  };
  const closeParticipants = () => { setParticipantsOpen(false); setParticipantsCourse(null); setParticipantsRows([]); };

  const openScheduleModal  = (course) => { setScheduleCourse(course); setScheduleOpen(true); };
  const closeScheduleModal = () => { setScheduleOpen(false); setScheduleCourse(null); };
  const openScheduleViewer  = (course) => { setScheduleViewerCourse(course); setScheduleViewerOpen(true); };
  const closeScheduleViewer = () => { setScheduleViewerCourse(null); setScheduleViewerOpen(false); };

  const handleScheduleSubmit = async (scheduleData) => {
    if (!scheduleCourse) return;
    try {
      await updateCourse(scheduleCourse.id, {
        schedule: scheduleData.weekly,
        scheduleMetadata: { sessionDuration: scheduleData.sessionDuration, repeatWeeks: scheduleData.repeatWeeks, totalSessions: scheduleData.totalSessions, weeklyHours: scheduleData.weeklyHours },
      });
      await reloadMyCourses();
      showAlert?.('מערכת השעות נשמרה בהצלחה', 'success');
      return true;
    } catch (e) { showAlert?.(e?.message || 'שגיאה בשמירת מערכת השעות', 'warning'); return false; }
  };

  const openSyllabusModal  = (course) => { setSyllabusCourse(course); setSyllabusOpen(true); };
  const closeSyllabusModal = () => { setSyllabusCourse(null); setSyllabusOpen(false); };

  const handleOpenSyllabusView = (course) => {
    if (!course) return;
    const raw = String(course.syllabusUrl || course.syllabus_url || '').trim();
    if (!raw) { showAlert?.('אין סילבוס זמין לקורס זה', 'warning'); return; }
    setSyllabusViewCourse({ ...course, syllabusUrl: raw });
  };
  const handleCloseSyllabusView = () => setSyllabusViewCourse(null);

  const openImagePicker  = (course) => { setImagePickerCourse(course); setImagePickerOpen(true); };
  const closeImagePicker = () => { setImagePickerCourse(null); setImagePickerOpen(false); };

  const openConfirmPublish = (course) => setConfirm({ open: true, mode: 'publish', course });
  const openConfirmArchive = (course) => setConfirm({ open: true, mode: 'archive', course });
  const closeConfirm = () => setConfirm({ open: false, mode: null, course: null });

  const handleRangeChange = async (start, end) => {
    const nextRange = { start, end };

    setRegisteredDateRange(nextRange);

    await fetchMyCoursesPage({
      position,
      page: 1,
      range: nextRange,
    });
  };

  const clearFilter = async () => {
    const nextRange = { start: '', end: '' };

    setRegisteredDateRange(nextRange);

    await fetchMyCoursesPage({
      position,
      page: 1,
      range: nextRange,
    });
  };

  useEffect(() => {
    fetchMyCoursesPage({ position, page: 1, range: registeredDateRange });
  }, [position, fetchMyCoursesPage]);

   
  const hasActiveRange = !!registeredDateRange.start || !!registeredDateRange.end;

  const counts = useMemo(() => ({ active: myCoursesTotalActive, archived: myCoursesTotalArchived }), [myCoursesTotalActive, myCoursesTotalArchived]);

  const displayCourses = useMemo(
    () => (position === POS.ARCHIVED ? getMyCoursesArchived() : getMyCoursesActive()),
    [position, getMyCoursesActive, getMyCoursesArchived]
  );

  const currentTotal = position === POS.ARCHIVED ? myCoursesTotalArchived : myCoursesTotalActive;
  const totalPages = Math.max(1, Math.ceil((currentTotal || 0) / (myCoursesLimit || 1)));

  // Header copy per role — same card/tab/pagination UI underneath,
  // just different framing since "my courses" means something
  // different for each of the four roles.
  const headerTitle =
    roleKind === 'supplier' ? 'קורסי מרכז הלמידה' :
    roleKind === 'school' ? 'קורסי התוכניות שלנו' :
    'הקורסים שלי';

  const headerSubtitle =
    roleKind === 'supplier' ? 'כל הקורסים הציבוריים וקורסי התוכניות שנוצרו על ידי מרכז הלמידה' :
    roleKind === 'school' ? 'קורסים בתוכניות הגפ״ן שבית הספר רשום אליהן' :
    roleKind === 'teacher' ? 'ארגן ונהל את הקורסים שלך בקלות' :
    'הקורסים שנרשמת אליהם';

  const emptyTitle =
    position === POS.ARCHIVED ? 'לא נמצאו קורסים בארכיון' :
    roleKind === 'teacher' ? 'אין לך קורסים פעילים' :
    roleKind === 'supplier' ? 'אין למרכז הלמידה קורסים פעילים' :
    roleKind === 'school' ? 'אין קורסים פעילים בתוכניות שלכם' :
    'אין קורסים פעילים';

  const emptyBody =
    position === POS.ARCHIVED
      ? (hasActiveRange ? 'לא נמצאו קורסים בארכיון בטווח התאריכים שנבחר.' : 'אין כרגע קורסים בארכיון.')
      : roleKind === 'teacher'
        ? (hasActiveRange ? 'לא נמצאו קורסים פעילים בטווח שנבחר.' : 'עדיין לא יצרת קורסים במערכת.')
      : roleKind === 'supplier'
        ? (hasActiveRange ? 'לא נמצאו קורסים פעילים בטווח שנבחר.' : 'עדיין לא נוצרו קורסים על ידי מרכז הלמידה.')
      : roleKind === 'school'
        ? (hasActiveRange ? 'לא נמצאו קורסים פעילים בטווח שנבחר.' : 'בית הספר עדיין לא רשום לתוכניות גפ״ן עם קורסים פעילים.')
        : (hasActiveRange ? 'לא נמצאו קורסים פעילים בטווח שנבחר.' : 'עדיין לא נרשמת לקורסים במערכת.');

  const api = useMemo(() => {
    const normalizeId   = (cid) => (typeof cid === 'object' && cid ? cid.id : cid);
    const extractStatus = (c) => typeof c === 'object' && c ? String(c.status || '').trim() : '';
    const safeRun = async (fn, okMsg) => {
      try { await fn(); if (showAlert && okMsg) showAlert(okMsg, 'success'); }
      catch (e) { const msg = e?.message || 'פעולה נכשלה.'; if (showAlert) showAlert(msg, 'warning'); else window.alert?.(msg); throw e; }
    };
    return {
      publishCourse: (c) => safeRun(async () => {
        const st = extractStatus(c);
        if (st && st !== 'Editing') throw new Error('ניתן לפרסם רק קורס במצב עריכה');
        await updateCourse(normalizeId(c), { status: 'Opened' }); await reloadMyCourses();
      }, 'הקורס פורסם בהצלחה'),
      archiveCourse: (c) => safeRun(async () => {
        const st = extractStatus(c);
        if (st && st !== 'Opened') throw new Error('ניתן לארכב רק קורס שפורסם');
        await updateCourse(normalizeId(c), { status: 'Closed' }); await reloadMyCourses();
      }, 'הקורס הועבר לארכיון'),
      editCourse: (c, payload) => safeRun(async () => { await updateCourse(normalizeId(c), payload); await reloadMyCourses(); }, 'הקורס עודכן בהצלחה'),
      uploadCourseSyllabus: (c, url) => safeRun(async () => {
        if (!url?.trim()) throw new Error('נא להזין כתובת URL של סילבוס');
        await updateCourse(normalizeId(c), { syllabus_url: String(url).trim() }); await reloadMyCourses();
      }, 'סילבוס הקורס עודכן בהצלחה'),
    };
  }, [updateCourse, reloadMyCourses, showAlert]);

  const handleConfirm = async () => {
    const c = confirm.course;
    try { if (confirm.mode === 'publish') await api.publishCourse(c); else if (confirm.mode === 'archive') await api.archiveCourse(c); }
    finally { closeConfirm(); }
  };

  const handleSyllabusSubmit = async (syllabusUrl) => {
    if (!syllabusCourse) return;
    try { await api.uploadCourseSyllabus(syllabusCourse, syllabusUrl); closeSyllabusModal(); } catch (e) { console.error(e); }
  };

  const handleCourseImageSubmit = async (imageUrl) => {
    if (!imagePickerCourse) return;
    try { await api.editCourse(imagePickerCourse.id, { image: imageUrl, image_url: imageUrl }); closeImagePicker(); } catch (e) { console.error(e); }
  };

  const openAssignTeacher = (course) => {
    setAssignTeacherCourse(course);
    setTeacherOptionsLoading(true);
    fetchMyTeamTeachers()
      .then(setTeacherOptions)
      .catch((err) => {
        console.error(err);
        showAlert?.(err.message || 'שגיאה בטעינת צוות המורים', 'warning');
        setTeacherOptions([]);
      })
      .finally(() => setTeacherOptionsLoading(false));
  };
  const closeAssignTeacher = () => setAssignTeacherCourse(null);
  const handleAssignTeacherSubmit = async (teacherId) => {
    if (!assignTeacherCourse) return;
    // Server re-validates that teacherId is on THIS supplier's active
    // team (CoursesController::update()) — this list is just what's
    // offered, not the source of truth.
    // api.editCourse already alerts on success/failure via safeRun, so
    // just log here rather than double-alert.
    try { await api.editCourse(assignTeacherCourse.id, { teacherId }); } catch (e) { console.error(e); }
  };

  const closeAssignProgram = () => setAssignProgramCourse(null);
  const handleAssignProgramSubmit = async (programId) => {
    if (!assignProgramCourse) return;
    // null programId means "make it public" — CoursesController::update()
    // maps that to ownership_type=SUPPLIER_INDEPENDENT/access_scope=PUBLIC,
    // a real programId maps to PROGRAM/PROGRAM_ONLY (see backend).
    try { await api.editCourse(assignProgramCourse.id, { programId }); } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (!isTeacher || !teacherId) { setTeacherBusySlots([]); return; }
    setTeacherBusySlots(buildTeacherBusySlots(getMyCoursesActive(), scheduleCourse?.id || null));
  }, [isTeacher, teacherId, getMyCoursesActive, scheduleCourse]);

 
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loadingMyCourses) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }, [myCoursesPage, loadingMyCourses]);

  const handleMyCoursesPageChange = async (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === myCoursesPage) return;
    await goToMyCoursesPage(nextPage, position);
  };

  return (
    <>
      <div className="md:min-h-screen bg-gray-100" dir="rtl">
        <MyCoursesHeader title={headerTitle} subtitle={headerSubtitle} />

        <div className="mx-auto max-w-8xl px-2 sm:px-4 md:px-6 lg:px-8 pt-4 space-y-2">
          <div className="pt-0 px-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="inline-flex overflow-hidden rounded-lg bg-gray-50 p-1">
                  {TAB_LIST.map(({ key, label, countKey }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setPosition(key)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                        position === key ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-700 hover:text-gray-900'
                      }`}
                    >
                      {label} ({counts[countKey]})
                    </button>
                  ))}
                </div>

                {/* Visible for teacher and supplier (a school never
                    creates courses). A supplier-created course still
                    starts with no assigned teacher — picking one from
                    the team roster and choosing public vs GEFEN program
                    isn't wired into this shared modal yet, so for now
                    it creates a plain SUPPLIER_INDEPENDENT course the
                    supplier can assign a teacher to afterward. */}
                {(isTeacher || isSupplier) && (
                  <button
                    type="button"
                    onClick={() => canCreateCourse && setIsCreateOpen(true)}
                    disabled={!canCreateCourse}
                    title={
                      canCreateCourse
                        ? 'יצירת קורס חדש'
                        : isTeacher
                          ? 'לא ניתן ליצור קורס עד לאישור המורה'
                          : 'לא ניתן ליצור קורס עד לאישור מרכז הלמידה על ידי הפלטפורמה'
                    }
                    aria-haspopup="dialog"
                    className={[
                      'shrink-0 inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      canCreateCourse ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-300 text-gray-600 cursor-not-allowed',
                    ].join(' ')}
                  >
                    <span className={['ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full', canCreateCourse ? 'bg-white/15' : 'bg-black/10'].join(' ')}>
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                        <path d="M12 4v16M20 12H4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                      </svg>
                    </span>
                    יצירת קורס חדש
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex flex-wrap items-end gap-2">
                <DateRangePicker
                  value={registeredDateRange}
                  onDateRangeChange={handleRangeChange}
                  minDate={-3650}
                  hasRange={hasActiveRange}
                  onClear={clearFilter}
                />
              </div>
            </div>
          </div>

          {hasActiveRange && (
            <div className="flex items-center gap-2 flex-shrink-0 px-2">
              <span className="text-sm text-gray-500">{TAB_LIST.find((t) => t.key === position)?.label} • בטווח שנבחר</span>
              <span className="inline-flex items-center justify-center rounded-full bg-gray-100 text-gray-700 text-xs font-bold px-2.5 py-1 min-w-[28px]">{currentTotal}</span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-600">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                סינון פעיל
              </span>
            </div>
          )}

          <div className="pt-4">
            {loadingMyCourses ? (
              <div className="relative min-h-[220px]">
                <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />
              </div>
            ) : displayCourses.length === 0 ? (
              <div className="flex flex-col items-center bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 justify-center py-16 text-center">
                <div className="mb-4 w-16 h-16 rounded-2xl bg-gray-50 border border-gray-200 flex items-center justify-center">
                  <svg className="h-7 w-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">{emptyTitle}</h3>
                <p className="text-sm text-gray-500 max-w-sm">{emptyBody}</p>
              </div>
            ) : (
              <>
                {/* Grid — Tailwind arbitrary values replace the <style jsx> block:
                    default (≤480px equiv): single column, gap-4
                    sm (≥640px): auto-fill minmax(320px), gap-5
                    md (≥768px): auto-fill minmax(360px), gap-6            */}

                
                {totalPages > 1 && (
                  <div className="flex items-center justify-start mb-3.5 px-3.5">
                    <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-xs">
                      עמוד {myCoursesPage} מתוך {totalPages}
                    </span> 
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(360px,1fr))] gap-4 sm:gap-5 md:gap-6">
                  {displayCourses.map((course) => {
                    // Ownership vs. registration vs. read-only viewing,
                    // per role:
                    //  - teacher: owns courses where they're the teacher_id
                    //  - supplier: owns courses it created (public or
                    //    program-based), regardless of assigned teacher
                    //  - school: neither owns nor registered — just
                    //    viewing the courses that live in its programs
                    //  - student (default): registered, not owner
                    const cardProps = {
                      course,
                      isRegistered: roleKind === 'student',
                      isOwner: roleKind === 'teacher' || roleKind === 'supplier',
                      search: false,
                      isMenuOpen: openMenuId === course.id,
                      onMenuOpen:  () => setOpenMenuId(course.id),
                      onMenuClose: () => setOpenMenuId(null),
                      onRequestEdit: (c) => { setEditingCourse(c); setOpenMenuId(null); },
                      onPublishCourseConfirm: (idOrObj) => { openConfirmPublish(typeof idOrObj === 'object' ? idOrObj : course); setOpenMenuId(null); },
                      onArchiveCourseConfirm: (idOrObj) => { openConfirmArchive(typeof idOrObj === 'object' ? idOrObj : course); setOpenMenuId(null); },
                      onAddScheduleConfirm: () => { openScheduleModal(course); setOpenMenuId(null); },
                      onShowParticipants: (c) => { openParticipants(c); setOpenMenuId(null); },
                      onShowSchedule:     (c) => { openScheduleViewer(c); setOpenMenuId(null); },
                      onOpenSyllabus:     (c) => { openSyllabusModal(c); setOpenMenuId(null); },
                      onViewSyllabus:     (c) => { handleOpenSyllabusView(c); setOpenMenuId(null); },
                      onChangeImage: (cOrId) => { openImagePicker(typeof cOrId === 'object' ? cOrId : course); setOpenMenuId(null); },
                      onAssignTeacher: () => { openAssignTeacher(course); setOpenMenuId(null); },
                      onAssignProgram: () => { setAssignProgramCourse(course); setOpenMenuId(null); },
                    };
                    if (isTeacher && teacherId) cardProps.ownerId = teacherId;
                    else if (isSupplier && user?.id) cardProps.ownerId = user.id;
                    return <CourseCard key={course.id} {...cardProps} />;
                  })}
                </div>

                <div className="flex justify-start mt-5 mb-4">
                  <Pagination 
                    page={myCoursesPage} 
                    totalPages={totalPages} 
                    onPageChange={handleMyCoursesPageChange} 
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <CreateCourseModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />

      <CreateCourseModal
        isOpen={!!editingCourse}
        onClose={() => setEditingCourse(null)}
        mode="edit"
        course={editingCourse}
        onSubmit={async ({ payload, id }) => { await api.editCourse(id, payload); setEditingCourse(null); }}
      />

      <ConfirmDialog
        isOpen={confirm.open}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        title={confirm.mode === 'publish' ? 'לאשר פרסום קורס?' : 'לאשר העברה לארכיון?'}
        body={confirm.course ? confirm.mode === 'publish' ? `האם לפרסם את "${confirm.course.title}" כך שיהיה זמין להרשמה לסטודנטים?` : `האם להעביר את "${confirm.course.title}" לארכיון? לאחר ארכוב הקורס לא יהיה זמין להרשמה.` : ''}
        confirmText={confirm.mode === 'publish' ? 'פרסם קורס' : 'העבר לארכיון'}
        cancelText="ביטול"
        danger={confirm.mode === 'archive'}
      />

      <ParticipantsModal isOpen={participantsOpen} onClose={closeParticipants} course={participantsCourse} rows={participantsRows} />

      <WeeklyScheduleModal
        isOpen={scheduleOpen}
        onClose={closeScheduleModal}
        onSubmit={handleScheduleSubmit}
        initialSchedule={scheduleCourse?.schedule}
        courseTitle={scheduleCourse?.title || 'קורס'}
        teacherBusySlots={teacherBusySlots}
      />

      <CourseScheduleModal isOpen={scheduleViewerOpen} onClose={closeScheduleViewer} schedule={scheduleViewerCourse?.schedule} courseTitle={scheduleViewerCourse?.title || 'קורס'} />

      <CourseSyllabusModal isOpen={syllabusOpen} onClose={closeSyllabusModal} courseTitle={syllabusCourse?.title || 'קורס'} onSubmit={handleSyllabusSubmit} initialUrl={syllabusCourse?.syllabusUrl || syllabusCourse?.syllabus_url || ''} />

      <CourseSyllabusViewerModal isOpen={!!syllabusViewCourse} onClose={handleCloseSyllabusView} courseTitle={syllabusViewCourse?.title} syllabusUrl={syllabusViewCourse?.syllabusUrl} />

      <CourseImagePickerModal isOpen={imagePickerOpen} onClose={closeImagePicker} onSubmit={handleCourseImageSubmit} courseTitle={imagePickerCourse?.title || 'קורס'} initialImage={imagePickerCourse?.image || ''} collections={courseImageCollections || []} />

      <AssignCourseTeacherModal
        isOpen={!!assignTeacherCourse}
        onClose={closeAssignTeacher}
        course={assignTeacherCourse}
        teachers={teacherOptions}
        loading={teacherOptionsLoading}
        onSubmit={handleAssignTeacherSubmit}
      />

      <AssignCourseProgramModal
        isOpen={!!assignProgramCourse}
        onClose={closeAssignProgram}
        course={assignProgramCourse}
        programs={confirmedPrograms}
        loading={supplierProgramsLoading}
        onSubmit={handleAssignProgramSubmit}
      />
    </>
  );
};

export default MyCourses;